import * as React from 'react';
import {
    autoUpdate,
    flip,
    shift,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useListNavigation,
    useRole,
    useTransitionStatus,
    useTypeahead,
} from '@floating-ui/react';
import { SelectMenuOptionData } from './SelectMenu.types';
import { useHoneycombTheme } from '../..';

export interface SelectMenuParams {
    value?: string;
    defaultValue?: string;
    onChange?: (value: string) => void;
    options: Array<SelectMenuOptionData>;
}

export function useSelectMenu(params: SelectMenuParams) {
    const { value: valueProp, defaultValue, onChange, options } = params;

    const [open, setOpen] = React.useState(false);

    const isControlled = valueProp !== undefined;
    const [valueState, setValueState] = React.useState(defaultValue);

    // The value may be controlled by the prop or uncontrolled using internal state.
    const getValue = React.useCallback(() => {
        return isControlled ? valueProp : valueState;
    }, [isControlled, valueProp, valueState]);

    // Synchronise the selected option with our current value.
    // Floating UI deals with a selected index whereas we control the component using the associated string value.
    const selectedOption = options.find((option) => option.value === getValue());

    const [activeIndex, setActiveIndex] = React.useState<number | null>(null);

    const listRef = React.useRef<Array<HTMLElement | null>>([]);
    const labelsRef = React.useRef<Array<string | null>>(options.map((option) => option.label));

    const isTypingRef = React.useRef(false);

    const handleOpenChange = React.useCallback((newOpen: boolean) => {
        setOpen(newOpen);
    }, []);

    const handleSelect = React.useCallback(
        (index: number) => {
            // Find the option associated with this index.
            const newOption = options.find((option) => option.index === index);
            if (!newOption) {
                return;
            }

            setOpen(false);

            if (!isControlled) {
                setValueState(newOption.value);
                return;
            }
            onChange?.(newOption.value);
        },
        [isControlled, onChange, options]
    );

    const handleTypeaheadMatch = React.useCallback(
        (index: number) => {
            if (open) {
                setActiveIndex(index);
            } else {
                handleSelect(index);
            }
        },
        [handleSelect, open]
    );

    const data = useFloating({
        open,
        onOpenChange: handleOpenChange,
        placement: 'bottom-start',
        middleware: [
            flip(),
            shift(),
            size({
                apply({ rects, elements, availableHeight }) {
                    const inner = elements.floating.firstChild as HTMLElement;
                    Object.assign(inner.style, {
                        maxHeight: `${availableHeight}px`,
                        minWidth: `${rects.reference.width}px`,
                    });
                },
                padding: 10,
            }),
        ],
        whileElementsMounted: autoUpdate,
    });

    const { context } = data;

    const listNav = useListNavigation(context, {
        listRef,
        activeIndex,
        selectedIndex: selectedOption?.index,
        onNavigate: setActiveIndex,
    });

    const typeahead = useTypeahead(context, {
        listRef: labelsRef,
        activeIndex,
        selectedIndex: selectedOption?.index,
        onMatch: handleTypeaheadMatch,
        onTypingChange(isTyping) {
            isTypingRef.current = isTyping;
        },
        ignoreKeys: !isTypingRef.current ? [' '] : undefined,
    });

    const { theme } = useHoneycombTheme();

    const { isMounted, status: transitionStatus } = useTransitionStatus(context, {
        duration: theme.components.themes.Popover.animationDuration,
    });

    const role = useRole(context, { role: 'listbox' });
    const dismiss = useDismiss(context);
    const click = useClick(context);

    const interactions = useInteractions([listNav, typeahead, role, dismiss, click]);

    return React.useMemo(
        () => ({
            open,
            ...data,
            ...interactions,
            listRef,
            activeIndex,
            handleSelect,
            selectedOption,
            isMounted,
            transitionStatus,
        }),
        [open, data, interactions, activeIndex, handleSelect, selectedOption, isMounted, transitionStatus]
    );
}
